import { AWSSESProvider } from './providers/AWSSESProvider';
import { MailgunProvider } from './providers/MailgunProvider';
import { SMTPProvider } from './providers/SMTPProvider';
import { APIEmailProvider } from './providers/APIEmailProvider';
import type { EmailProvider, IEmailProvider } from './types';
import { authLogger } from '../../utils/logger';

/**
 * Factory class for creating email providers
 */
export class EmailProviderFactory {
  private static instance: EmailProviderFactory;
  private providers: Map<EmailProvider, IEmailProvider> = new Map();

  private constructor() {
    this.initializeProviders();
  }

  /**
   * Get singleton instance
   */
  static getInstance(): EmailProviderFactory {
    if (!EmailProviderFactory.instance) {
      EmailProviderFactory.instance = new EmailProviderFactory();
    }
    return EmailProviderFactory.instance;
  }

  /**
   * Initialize all providers
   */
  private initializeProviders(): void {
    this.providers.set('API', new APIEmailProvider());
    this.providers.set('AWS_SES', new AWSSESProvider());
    this.providers.set('MAILGUN', new MailgunProvider());
    this.providers.set('SMTP', new SMTPProvider());
  }

  /**
   * Get email provider from environment configuration
   */
  private getConfiguredProvider(): EmailProvider {
    const provider = import.meta.env.VITE_EMAIL_PROVIDER?.toUpperCase();
    
    if (provider === 'API' || provider === 'AWS_SES' || provider === 'MAILGUN' || provider === 'SMTP') {
      return provider as EmailProvider;
    }
    
    // Default to API if not specified
    return 'API';
  }

  /**
   * Get the configured email provider instance
   */
  getProvider(): IEmailProvider {
    const providerType = this.getConfiguredProvider();
    const provider = this.providers.get(providerType);
    
    if (!provider) {
      throw new Error(`Email provider ${providerType} not found`);
    }

    // Validate configuration
    if (!provider.validateConfiguration()) {
      authLogger.warn(`⚠️ Email provider ${providerType} configuration is invalid`);
      
      // Try to find a working fallback provider
      const fallbackProvider = this.findWorkingProvider();
      if (fallbackProvider) {
        authLogger.info(`🔄 Falling back to ${fallbackProvider.getProviderName()}`);
        return fallbackProvider;
      }
      
      // If no fallback works, still return the configured provider
      // It will handle the error gracefully
      authLogger.warn(`⚠️ No working email provider found, using ${providerType} anyway`);
    }

    return provider;
  }

  /**
   * Find a working provider as fallback
   */
  private findWorkingProvider(): IEmailProvider | null {
    const providerOrder: EmailProvider[] = ['API', 'AWS_SES', 'MAILGUN', 'SMTP'];
    
    for (const providerType of providerOrder) {
      const provider = this.providers.get(providerType);
      if (provider && provider.validateConfiguration()) {
        return provider;
      }
    }
    
    return null;
  }

  /**
   * Get specific provider by type (for testing)
   */
  getProviderByType(type: EmailProvider): IEmailProvider {
    const provider = this.providers.get(type);
    if (!provider) {
      throw new Error(`Email provider ${type} not found`);
    }
    return provider;
  }

  /**
   * Get all available providers with their status
   */
  getProviderStatus(): Record<EmailProvider, { available: boolean; configured: boolean }> {
    const status: Record<EmailProvider, { available: boolean; configured: boolean }> = {
      API: { available: false, configured: false },
      AWS_SES: { available: false, configured: false },
      MAILGUN: { available: false, configured: false },
      SMTP: { available: false, configured: false }
    };

    for (const [type, provider] of this.providers) {
      status[type] = {
        available: true,
        configured: provider.validateConfiguration()
      };
    }

    return status;
  }

  /**
   * Test all providers
   */
  async testAllProviders(): Promise<Record<EmailProvider, { success: boolean; message: string }>> {
    const results: Record<EmailProvider, { success: boolean; message: string }> = {
      API: { success: false, message: 'Not tested' },
      AWS_SES: { success: false, message: 'Not tested' },
      MAILGUN: { success: false, message: 'Not tested' },
      SMTP: { success: false, message: 'Not tested' }
    };

    const testData = {
      to: import.meta.env.VITE_SEND_TO_EMAIL || 'info@fabrik28.ai',
      subject: 'Email Provider Test',
      message: 'This is a test email to verify provider functionality.',
      userEmail: 'test@fabrik28.ai',
      userName: 'Test User',
      timestamp: new Date().toISOString(),
      context: 'test'
    };

    for (const [type, provider] of this.providers) {
      try {
        if (provider.validateConfiguration()) {
          const result = await provider.sendEmail(testData);
          results[type] = {
            success: result.success,
            message: result.message
          };
        } else {
          results[type] = {
            success: false,
            message: 'Configuration invalid'
          };
        }
      } catch (error: any) {
        results[type] = {
          success: false,
          message: error.message || 'Unknown error'
        };
      }
    }

    return results;
  }
}
